import { Link as RouterLink } from 'react-router-dom'
import { Box, ButtonBase, Link, Stack, TableBody, TableHead, TableRow, Typography, keyframes } from '@mui/material'
import { ArrowUpRightIcon } from 'lucide-react'

import { Card, Skeleton } from '@repo/ui'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { linkedSearchState, useReferrerState } from 'hooks'
import { useWebsiteLeads, useWebsiteLive } from 'hooks/api'
import { ROUTES } from 'routes'
import type { WebsiteLeadRow } from 'services'

import { AUDIENCE_LABELS, referrerLabel } from '../websiteAnalyticsModel'
import {
  Cell,
  EmptyRow,
  HeadCell,
  SectionNote,
  shortDate,
  SourceNotices,
  StatTable,
  ToneChip,
  useTone,
} from './shared'
import { WebsiteSessionTable } from './WebsiteSessionTable'

const TableSkeleton = () => (
  <Stack spacing={2} sx={{ mt: 4 }}>
    <Skeleton height={14} lines={4} />
  </Stack>
)

const errorText = (error: unknown, what: string): string =>
  (error as { status?: number } | null)?.status === 403
    ? `You cannot view ${what}.`
    : `Could not load ${what}. Reload the page, or check the API is running.`

// ── Live now ─────────────────────────────────────────────────────────────────────

/** The Live now card's anchor: the header KPI scrolls here. */
export const LIVE_NOW_ANCHOR_ID = 'website-live-now'

const livePulse = keyframes`
  0% { box-shadow: 0 0 0 0 currentColor; }
  70% { box-shadow: 0 0 0 6px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`

/**
 * Header KPI: "● 3 on the site now". Independent of the page's range — it is the same
 * 30-second live read as the Live now card (one react-query cache entry, one request).
 * A failed read shows "—" and says so; it is never rendered as 0, which would claim the
 * site is empty when we simply do not know.
 */
export const LiveNowKpi = ({ onClick }: { onClick: () => void }) => {
  const { data, isLoading, error } = useWebsiteLive()
  const tone = useTone()
  const failed = !!error || (!isLoading && !data) || data?.posthog.status === 'error'
  const count = data && !failed ? data.sessions.length : null
  const swatch = tone(failed ? 'negative' : count ? 'positive' : 'neutral')
  const label = isLoading
    ? 'Checking who is on the site'
    : failed
      ? 'Live count unavailable'
      : `${count} on the site now`
  return (
    <InfoTooltip
      text={
        failed
          ? 'PostHog did not answer, so the live count is unknown (not zero). Click for details.'
          : 'Visitors with analytics on (PostHog) active in the last 5 minutes. Refreshes every 30 seconds. Click to see who.'
      }
    >
      <ButtonBase
        onClick={onClick}
        aria-label={`${label}. Show the Live now list`}
        data-testid="live-now-kpi"
        sx={{
          gap: 1.5,
          px: 2.5,
          height: 36,
          borderRadius: 999,
          // `statusTone` output: the same mode-aware border / fill / text a StatusChip uses.
          border: `1px solid ${swatch.border}`,
          background: swatch.background,
          color: swatch.color,
          typography: 'body2',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        <Box
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'currentColor',
            flexShrink: 0,
            // Pulses only when someone is actually there; respects reduced motion.
            ...(count
              ? {
                  animation: `${livePulse} 2s ease-out infinite`,
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }
              : {}),
          }}
        />
        <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
          {isLoading ? '…' : failed ? '—' : count}
        </Typography>
        <Typography component="span" variant="body2" sx={{ color: 'inherit' }}>
          {failed ? 'unavailable' : 'on the site now'}
        </Typography>
      </ButtonBase>
    </InfoTooltip>
  )
}

export const WebsiteLiveCard = () => {
  const { data, isLoading, error } = useWebsiteLive()
  return (
    <Box id={LIVE_NOW_ANCHOR_ID} sx={{ scrollMarginTop: 24 }}>
      <Card
        title="Live now"
        action={
          data ? (
            <ToneChip
              tone={data.sessions.length ? 'positive' : 'neutral'}
              label={`${data.sessions.length} on the site`}
            />
          ) : undefined
        }
      >
        {isLoading ? (
          <TableSkeleton />
        ) : error || !data ? (
          <Box sx={{ mt: 4 }}>
            <SectionNote>{errorText(error, 'live visitors')}</SectionNote>
          </Box>
        ) : (
          <Stack spacing={3} sx={{ mt: 4 }}>
            <SectionNote>
              {AUDIENCE_LABELS.consented} with activity in the last {data.windowMinutes} minutes.
              Visitors whose analytics are off are on the site too but cannot be listed. Refreshes
              every 30 seconds.
            </SectionNote>
            <SourceNotices sources={[['PostHog', data.posthog]]} />
            <WebsiteSessionTable
              sessions={data.sessions}
              emptyText="Nobody with analytics on is on the site right now."
            />
          </Stack>
        )}
      </Card>
    </Box>
  )
}

// Visitor history moved to WebsiteVisitorHistoryCard.tsx (VRG-712: server-side pages, journey).

// ── Leads (platform admins) ──────────────────────────────────────────────────────

const Step = ({ at }: { at: string | null }) => (
  <Cell>{at ? shortDate(at) : '—'}</Cell>
)

/**
 * VRG-712 — where else this person is. The API matches the journey's email to Access and to
 * Sales → People server-side (admins only; VRG-727 renamed both) and returns a key for each match; a link appears only
 * for a match. The email travels in router state (`linkedSearchState`), never the URL, and seeds
 * the destination list's search, so the list opens on this one person.
 */
const LeadLinks = ({ lead, referrer }: { lead: WebsiteLeadRow; referrer: ReturnType<typeof useReferrerState> }) => {
  if (!lead.peopleKey && !lead.salesLeadId) return <Cell>—</Cell>
  return (
    <Cell>
      <Stack direction="row" spacing={3} component="span" sx={{ display: 'inline-flex' }}>
        {lead.peopleKey ? (
          <Link
            component={RouterLink}
            to={ROUTES.INTERNAL_ACCESS}
            state={linkedSearchState(lead.peopleKey, referrer)}
            underline="hover"
            data-testid="lead-journey-people-link"
          >
            Access
          </Link>
        ) : null}
        {lead.salesLeadId ? (
          <Link
            component={RouterLink}
            to={`${ROUTES.INTERNAL_GTM_SALES}?tab=people`}
            state={linkedSearchState(lead.email, referrer)}
            underline="hover"
            data-testid="lead-journey-sales-link"
          >
            Sales
          </Link>
        ) : null}
      </Stack>
    </Cell>
  )
}

const LeadRow = ({ lead, referrer }: { lead: WebsiteLeadRow; referrer: ReturnType<typeof useReferrerState> }) => (
  <TableRow>
    <Cell>
      {lead.email}
      {lead.posthogPersonUrl ? (
        <InfoTooltip text="Open the PostHog person (their sessions and recordings)">
          <Link
            href={lead.posthogPersonUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ ml: 1, verticalAlign: 'middle' }}
            aria-label="Open the PostHog person"
          >
            <ArrowUpRightIcon size={14} />
          </Link>
        </InfoTooltip>
      ) : null}
    </Cell>
    <LeadLinks lead={lead} referrer={referrer} />
    <Cell>
      {lead.firstUtmSource
        ? `utm: ${lead.firstUtmSource}${lead.firstUtmCampaign ? ` / ${lead.firstUtmCampaign}` : ''}`
        : lead.firstReferrer !== null
          ? referrerLabel(lead.firstReferrer)
          : '—'}
    </Cell>
    <Cell>{lead.landingPath ?? '—'}</Cell>
    <Cell>{lead.country ?? '—'}</Cell>
    <Step at={lead.firstSeenAt} />
    <Step at={lead.formSubmittedAt ?? lead.formOpenedAt} />
    <Step at={lead.roiCalculatedAt ?? lead.roiStartedAt} />
    <Cell>{lead.signupStatus ? `${lead.signupStatus}` : '—'}</Cell>
    <Step at={lead.demoAt} />
    <Cell>{lead.companyName ?? (lead.companyId ? 'Company' : '—')}</Cell>
    <Step at={lead.becameClientAt} />
    <Cell>
      {/* Both chips explain themselves from the glossary (§44/§59), not from text typed here. */}
      {lead.becameClientTracked ? (
        <ToneChip tone="positive" label="Tracked" glossaryId="became-client-tracked" />
      ) : lead.posthogPersonUrl ? (
        <ToneChip tone="neutral" label="In PostHog" glossaryId="lead-in-posthog" />
      ) : (
        '—'
      )}
    </Cell>
  </TableRow>
)

/**
 * Lead journeys (platform admins). VRG-712 (Spencer, 2026-10-16): renamed from "Leads" so it is
 * not read as Sales → Leads or People, moved to the Visitors tab (Overview is aggregates only),
 * and each row links to the same person in Access and Sales → People where they exist (VRG-727 names).
 */
export const WebsiteLeadsCard = () => {
  const { data, isLoading, error } = useWebsiteLeads(true)
  const referrer = useReferrerState('Analytics')
  return (
    <Card
      title="Lead journeys"
      action={
        <Stack direction="row" spacing={2} alignItems="center">
          <InfoTooltip term="lead-journey" />
          <ToneChip tone="neutral" label="Admins only" />
        </Stack>
      }
    >
      {isLoading ? (
        <TableSkeleton />
      ) : error || !data ? (
        <Box sx={{ mt: 4 }}>
          <SectionNote>{errorText(error, 'leads')}</SectionNote>
        </Box>
      ) : (
        <Stack spacing={3} sx={{ mt: 4 }}>
          <SectionNote>
            One row per lead email, from vera-2’s own record: signups, grants, demos and
            clients from vera-2; first touch and form / ROI steps only for the audience “
            {AUDIENCE_LABELS.consented}”. Kept {Math.round(data.retentionDays / 365)}{' '}
            years after the last contact, unless the lead became a client. Showing{' '}
            {data.leads.length} of {data.total}, most recent contact first. “Tracked” = the{' '}
            <code>became_client</code> event reached PostHog (at the company’s first paid invoice).
            “Also in” opens the same person in Access or Sales → People, matched by email.
          </SectionNote>
          <StatTable>
            <TableHead>
              <TableRow>
                <HeadCell>Email</HeadCell>
                <HeadCell>Also in</HeadCell>
                <HeadCell>First source</HeadCell>
                <HeadCell>Landing page</HeadCell>
                <HeadCell>Country</HeadCell>
                <HeadCell>First seen</HeadCell>
                <HeadCell>Form</HeadCell>
                <HeadCell>ROI</HeadCell>
                <HeadCell>Signup</HeadCell>
                <HeadCell>Demo</HeadCell>
                <HeadCell>Company</HeadCell>
                <HeadCell>
                  Client
                  <InfoTooltip term="became-client" />
                </HeadCell>
                <HeadCell>PostHog</HeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.leads.length === 0 ? (
                <EmptyRow colSpan={13} text="No lead journeys recorded yet. The nightly snapshot fills this." />
              ) : (
                data.leads.map((lead) => <LeadRow key={lead.email} lead={lead} referrer={referrer} />)
              )}
            </TableBody>
          </StatTable>
        </Stack>
      )}
    </Card>
  )
}
